import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../api';

interface Customer {
  tenantID: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  subscriptionType: string;
}

interface EditCustomerDialogProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSave: () => void;
}

const subscriptionTypes = [
  { value: 'Trial', label: 'Trial' },
  { value: 'Standard', label: 'Standard' },
  { value: 'Premium', label: 'Premium' },
  { value: 'MSP', label: 'MSP' },
];

const EditCustomerDialog: React.FC<EditCustomerDialogProps> = ({
  open,
  customer,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    subscriptionType: 'Standard',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Populate form when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        companyName: customer.companyName || '',
        contactName: customer.contactName || '',
        contactEmail: customer.contactEmail || '',
        contactPhone: customer.contactPhone || '',
        subscriptionType: customer.subscriptionType || 'Standard',
      });
      setValidationErrors({});
      setError(null);
    }
  }, [customer]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    }

    if (!formData.contactName.trim()) {
      errors.contactName = 'Contact name is required';
    }

    if (!formData.contactEmail.trim()) {
      errors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      errors.contactEmail = 'Invalid email format';
    }

    if (formData.contactPhone && !/^[\d\s\-\(\)\+]+$/.test(formData.contactPhone)) {
      errors.contactPhone = 'Invalid phone number format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: '' });
    }
  };

  const handleSubmit = async () => {
    if (!customer) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.put(
        `/TCAdminUpdateCustomer/${customer.tenantID}`,
        formData
      );

      onSave();
      onClose();
    } catch (err: any) {
      console.error('Error updating customer:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to update customer. Please try again.';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        companyName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        subscriptionType: 'Standard',
      });
      setValidationErrors({});
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Customer</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Company Name"
            value={formData.companyName}
            onChange={handleChange('companyName')}
            error={!!validationErrors.companyName}
            helperText={validationErrors.companyName}
            fullWidth
            required
            disabled={loading}
          />

          <TextField
            label="Contact Name"
            value={formData.contactName}
            onChange={handleChange('contactName')}
            error={!!validationErrors.contactName}
            helperText={validationErrors.contactName}
            fullWidth
            required
            disabled={loading}
          />

          <TextField
            label="Contact Email"
            type="email"
            value={formData.contactEmail}
            onChange={handleChange('contactEmail')}
            error={!!validationErrors.contactEmail}
            helperText={validationErrors.contactEmail}
            fullWidth
            required
            disabled={loading}
          />

          <TextField
            label="Contact Phone"
            value={formData.contactPhone}
            onChange={handleChange('contactPhone')}
            error={!!validationErrors.contactPhone}
            helperText={validationErrors.contactPhone}
            fullWidth
            disabled={loading}
          />

          <TextField
            select
            label="Subscription Type"
            value={formData.subscriptionType}
            onChange={handleChange('subscriptionType')}
            fullWidth
            required
            disabled={loading}
          >
            {subscriptionTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCustomerDialog;
